import { CheckCircleFilled } from '@ant-design/icons';
import { Alert, Button, Divider, Image } from 'antd';
import AddToSlackButton from 'components/addToSlackButton';
import AnimatedDot from 'components/animatedDot';
import PageWrapper from 'components/pageWrapper';
import dayjs from 'dayjs';
import { useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import ProfileContext from 'services/profileContext';
import styles from 'styles/onboarding.module.scss';

const Onboarding = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const { profile } = useContext(ProfileContext);
  const location = useLocation();

  const onClickNext = () => {
    navigate('/create-cycle');
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('slackSuccess') === 'true') {
      setStep(2);
    }
  }, [location.search]);

  const getContent = () => {
    switch (step) {
      case 1:
      default:
        return (
          <div className={styles.onboardingContent}>
            <div className={styles.onboardingContentHeader}>🙋</div>
            <h1>Welcome to SimplePerf</h1>
            <div className={styles.onboardingContentDescription}>
              <div>
                The most effective way to run 360 reviews as an SMB without the
                clutter of complex and expensive platforms
              </div>
              <div className={styles.onboardingContentDescriptionItems}>
                <div className={styles.onboardingContentDescriptionItem}>
                  <div>
                    <CheckCircleFilled style={{ color: '#00d300' }} />
                  </div>
                  <div>
                    <b>Relevant</b>: Helps managers and employees fill in their
                    part to make it faster and have relevant content (AI follow
                    up questions)
                  </div>
                </div>
                <div className={styles.onboardingContentDescriptionItem}>
                  <div>
                    <CheckCircleFilled style={{ color: '#00d300' }} />
                  </div>
                  <div>
                    <b>On-time</b>: Smart reminders to make sure every part is
                    filled in when needed
                  </div>
                </div>

                <div className={styles.onboardingContentDescriptionItem}>
                  <div>
                    <CheckCircleFilled style={{ color: '#00d300' }} />
                  </div>
                  <div>
                    <b>Simple</b>: Launch easily without thousands of options,
                    keep the control on the operation
                  </div>
                </div>
              </div>
              <div>
                <b>First time doing perf review?</b> We’ll guide you through the
                process to create a great perf review.
              </div>
            </div>
            <div>
              <Alert
                showIcon={true}
                type="info"
                message="We’ll create a first review cycle together. It only takes 2 steps and a few clicks"
              />
            </div>
            <div style={{ width: '100%' }}>
              <Button onClick={() => setStep(2)} type="primary" block>
                Get Started
              </Button>
            </div>
          </div>
        );
      case 2:
        return (
          <div className={styles.onboardingContent}>
            <div className={styles.onboardingContentHeader}>
              <Image src={'/img/slack_80.png'} alt="Slack" preview={false} />
            </div>
            <h1>Connect your Slack workspace</h1>
            <div className={styles.onboardingContentDescription}>
              <div>
                Connecting your Slack workspace is the best way to:
                <ul>
                  <li>
                    <b>Choose the participants</b> of the performance reviews
                  </li>
                  <li>
                    <b>Communicate reminders and results.</b>
                  </li>
                </ul>
              </div>
              {!profile.workspace.slack ? (
                <div
                  style={{
                    textAlign: 'center',
                    marginTop: '36px',
                    marginBottom: '36px',
                  }}
                >
                  <AddToSlackButton size="large" param="onboarding" />
                  <div>
                    <Button
                      onClick={onClickNext}
                      type="link"
                      block
                      style={{ marginTop: 16 }}
                    >
                      Skip
                    </Button>
                  </div>
                </div>
              ) : (
                <div style={{ marginTop: 36 }}>
                  <Divider />
                  Your Slack workspace is successfully connected
                  <div className={styles.slackIntegrationRunning}>
                    <div className={styles.slackIntegrationRunningTitle}>
                      <Image
                        src="/img/slack_80.png"
                        width={20}
                        preview={false}
                      />
                      &nbsp;&nbsp;{profile.workspace.slack.name}
                    </div>
                    <div className={styles.slackIntegrationRunningDot}>
                      <AnimatedDot />{' '}
                      <div className={styles.slackIntegrationRunningText}>
                        Running
                      </div>
                    </div>
                    <div className={styles.slackIntegrationRunningLastSync}>
                      Last sync:{' '}
                      {dayjs(profile.workspace.slack.lastSync).format(
                        'DD/MM/YYYY HH:mm'
                      )}
                    </div>
                  </div>
                  <div style={{ marginTop: 36 }}>
                    <Button onClick={onClickNext} type="primary" block>
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        );
    }
  };

  return (
    <div className={styles.onboarding}>
      <PageWrapper width={600}>{getContent()}</PageWrapper>
    </div>
  );
};

export default Onboarding;
